import { FC, useEffect, useRef, useState } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../hooks/redux-hooks";
import {
  selectMainState,
  setIdTrack,
  setPlayRadio,
  setPreRollPlayed,
  setTrackState,
} from "../../../../../store/slices/main";
import "./style.css";
import { updatePlayCount } from "../../../../../api/asyncThuncks/audioTracks";
import {
  selectPlaylists,
  setSettingPlaylistPlaying,
  setSettingPlaylistTrackNumber,
} from "../../../../../store/slices/playlists";

type Props = {
  type: string;
};

const AudioPlayer: FC<Props> = ({ type }) => {
  const dispatch = useAppDispatch();
  const {
    radioPlayer: { radio, preRollPlayed },
    audioPlayer: { trackState, playForTrack },
    idPlayingTrack: { trackLink, idTrack },
  } = useAppSelector(selectMainState);
  const { trackNumber, nextTrackAuto, playlistByUrl, stopPlayingPlaylist } =
    useAppSelector(selectPlaylists);

  const audioRef = useRef<any>(null);
  const playerRef = useRef<videojs.Player>();
  const [isUpdatePlayCount, setIsUpdatePlayCount] = useState(false);
  const [volume, setVolume] = useState<number>(() => {
    const savedVolume = window.localStorage.getItem("volume");
    return savedVolume ? Number(savedVolume) / 100 : 0.5;
  });

  useEffect(() => {
    const audioElement = audioRef.current;
    if (audioElement) {
      const playerOptions: videojs.PlayerOptions = {
        defaultVolume: volume,
      };

      const player = videojs(audioElement, playerOptions);
      playerRef.current = player;

      let isCheckTime = false;

      player.on("timeupdate", () => {
        if (playerRef.current) {
          const currentTime = playerRef.current.currentTime();
          if (currentTime >= 15 && !isCheckTime) {
            setIsUpdatePlayCount(true);
            isCheckTime = true;
          }
        }
      });

      player.on("volumechange", () => {
        if (playerRef.current) {
          const newVolume = playerRef.current.volume();
          setVolume(newVolume);
          window.localStorage.setItem("volume", `${newVolume * 100}`);
        }
      });
      player.on("timeupdate", () => {
        if (playerRef.current) {
          const currentTime = playerRef.current.currentTime();
          const newTrackState = { ...trackState, currentTime };
          dispatch(setTrackState(newTrackState));
        }
      });
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = undefined;
      }
    };
  }, []);

  useEffect(() => {
    // The player stays mounted while idle, before a track has been selected.
    if (!playerRef.current || !trackLink?.trim()) return;

    if (playerRef.current && trackState?.currentTime !== null) {
      if (trackState?.currentTime) {
        playerRef.current.currentTime(trackState?.currentTime);
      }
      playerRef.current.play();
    }
    if (playerRef.current) {
      playerRef.current.src({
        src: trackLink,
        type: "application/x-mpegURL",
      });
      playerRef.current.load();
      playerRef.current.volume(volume);
      playerRef.current.on("ended", () => {
        if (preRollPlayed) {
          dispatch(setPlayRadio(true));
          dispatch(setPreRollPlayed(true));
        }
        if (stopPlayingPlaylist === false) {
          dispatch(setSettingPlaylistTrackNumber(trackNumber + 1));
          dispatch(setSettingPlaylistPlaying(true));
        }
      });
    }
  }, [trackLink]);

  useEffect(() => {
    if (
      playerRef.current &&
      trackNumber === playlistByUrl?.audiotracks.length
    ) {
      playerRef.current.pause();
      playerRef.current.trigger("stop");
      playerRef.current.off("stop");
      playerRef.current.hasStarted(false);
      playerRef.current.currentTime(0);
    }
  }, [nextTrackAuto]);

  useEffect(() => {
    if (playerRef.current && preRollPlayed) {
      playerRef.current.pause();
      playerRef.current.trigger("stop");
      playerRef.current.off("stop");
      playerRef.current.hasStarted(false);
      playerRef.current.currentTime(0);
    }
  }, [preRollPlayed]);

  useEffect(() => {
    if (playerRef.current && stopPlayingPlaylist) {
      playerRef.current.pause();
      playerRef.current.trigger("stop");
      playerRef.current.off("stop");
      playerRef.current.hasStarted(false);
      playerRef.current.currentTime(0);
    }
  }, [stopPlayingPlaylist]);

  useEffect(() => {
    if (playerRef.current && !playForTrack) {
      playerRef.current.pause();
      playerRef.current.trigger("stop");
      playerRef.current.off("stop");
      playerRef.current.hasStarted(false);
      playerRef.current.currentTime(0);
    }
  }, [playForTrack]);

  useEffect(() => {
    if (playerRef.current && radio) {
      playerRef.current.pause();
      playerRef.current.trigger("stop");
      playerRef.current.off("stop");
      playerRef.current.hasStarted(false);
      playerRef.current.currentTime(0);
    }
  }, [radio]);

  useEffect(() => {
    if (isUpdatePlayCount) {
      if (idTrack) {
        dispatch(updatePlayCount({ id: idTrack }));
      }
    }
  }, [isUpdatePlayCount]);

  const desctop = "vjs-desctop";
  const fullScreen = "vjs-full";

  return (
    <div>
      <audio
        className={`w-full ${type === "desctop" ? desctop : ""} ${
          type === "full" ? fullScreen : ""
        } vjs video-js js-playing vjs-has-started`}
        ref={audioRef}
        controls
        preload="auto"
        autoPlay={true}
      />
    </div>
  );
};

export default AudioPlayer;
